import React from "react";
import {
  SimpleGrid,
  Paper,
  Text,
  createStyles,
  Center,
  Skeleton,
  Stack,
  RingProgress,
} from "@mantine/core";
import { IconUserCheck, IconCalendarEvent } from "@tabler/icons-react";
import { useGetDirectorTeamStats } from "../../../api/attendance/getDirectorTeamStats";
import { useGetDirectorTeamLeaveStats } from "../../../api/leave/getDirectorTeamLeaveStats";

const useStyles = createStyles((theme) => ({
  root: {
    padding: theme.spacing.xl * 1.5,
  },

  label: {
    fontFamily: `Greycliff CF, ${theme.fontFamily as string}`,
  },

  card: {
    cursor: "default",
    transition: "none",
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
}));

interface StatsProps {
  onWidgetClick?: (type: "attendance" | "leaves") => void;
  activeTab?: "attendance" | "leaves";
}

export function DirectorStats({ onWidgetClick, activeTab }: StatsProps) {
  const { classes } = useStyles();
  const { data: statsResponse, isLoading } = useGetDirectorTeamStats();
  const { data: leaveStatsResponse, isLoading: isLeaveStatsLoading } =
    useGetDirectorTeamLeaveStats();

  // Build attendance stats from real data
  const stats = statsResponse?.data;
  const attendanceStats = [
    {
      label: "Today's Attendance",
      count: `${stats?.todayAttendance.present || 0}/${stats?.todayAttendance.total || 0}`,
      progress: stats?.todayAttendance.presentPercentage || 0,
      color: "teal",
      icon: IconUserCheck,
    },
    {
      label: "Yesterday",
      count: `${stats?.yesterdayAttendance.present || 0}`,
      progress: stats?.yesterdayAttendance.presentPercentage || 0,
      color: "blue",
      icon: IconUserCheck,
    },
    {
      label: "Last 7 Days (Avg)",
      count: `${Math.round(stats?.weeklyAverage.avgPresent || 0)}`,
      progress: stats?.weeklyAverage.avgPercentage || 0,
      color: "indigo",
      icon: IconUserCheck,
    },
    {
      label: "Late Today",
      count: `${stats?.lateToday || 0}`,
      progress:
        ((stats?.lateToday || 0) / (stats?.todayAttendance.total || 1)) * 100,
      color: "red",
      icon: IconUserCheck,
    },
    {
      label: "On Time",
      count: `${stats?.onTimeToday || 0}`,
      progress:
        ((stats?.onTimeToday || 0) / (stats?.todayAttendance.total || 1)) * 100,
      color: "green",
      icon: IconUserCheck,
    },
  ];

  // Build leave stats from real data
  const leaveStats = [
    {
      label: "Pending Requests",
      count: leaveStatsResponse?.data?.pendingRequests || 0,
      progress: 0,
      color: "orange",
      icon: IconCalendarEvent,
    },
    {
      label: "Approved (Today)",
      count: leaveStatsResponse?.data?.approvedToday || 0,
      progress: 0,
      color: "cyan",
      icon: IconCalendarEvent,
    },
    {
      label: "On Leave Today",
      count: leaveStatsResponse?.data?.onLeaveToday || 0,
      progress: 0,
      color: "grape",
      icon: IconCalendarEvent,
    },
    {
      label: "Rejected (7 Days)",
      count: leaveStatsResponse?.data?.rejectedLast7Days || 0,
      progress: 0,
      color: "red",
      icon: IconCalendarEvent,
    },
    {
      label: "Upcoming Leaves",
      count: leaveStatsResponse?.data?.upcomingLeaves || 0,
      progress: 0,
      color: "violet",
      icon: IconCalendarEvent,
    },
  ];

  if (isLoading || isLeaveStatsLoading) {
    return (
      <>
        <Text size="lg" fw={700} mb="sm" mt="md">
          Attendance Overview
        </Text>
        <SimpleGrid
          cols={5}
          breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          mb="xl"
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={90} radius="md" />
          ))}
        </SimpleGrid>
        <Text size="lg" fw={700} mb="sm">
          Leave Overview
        </Text>
        <SimpleGrid
          cols={5}
          breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          mb="xl"
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={90} radius="md" />
          ))}
        </SimpleGrid>
      </>
    );
  }

  return (
    <>
      <Text size="lg" fw={700} mb="sm" mt="md">
        Attendance Overview
      </Text>
      <SimpleGrid cols={5} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mb="xl">
        {attendanceStats.map((stat) => (
          <Paper
            withBorder
            radius="md"
            p={4}
            key={stat.label}
            className={classes.card}
            onClick={() => onWidgetClick?.("attendance")}
          >
            <Stack align="center" spacing={4}>
              <RingProgress
                size={60}
                roundCaps
                thickness={6}
                sections={[{ value: stat.progress, color: stat.color }]}
                label={
                  <Center>
                    <stat.icon size={16} stroke={1.5} />
                  </Center>
                }
              />

              <Stack align="center" spacing={0}>
                <Text fw={700} size="md">
                  {stat.count}
                </Text>
                <Text
                  c="dimmed"
                  tt="uppercase"
                  fw={600}
                  align="center"
                  style={{ lineHeight: 1.2 }}
                >
                  {stat.label}
                </Text>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>

      <Text size="lg" fw={700} mb="sm">
        Leave Overview
      </Text>
      <SimpleGrid cols={5} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mb="xl">
        {leaveStats.map((stat) => (
          <Paper
            withBorder
            radius="md"
            p={4}
            key={stat.label}
            className={classes.card}
            onClick={() => onWidgetClick?.("leaves")}
          >
            <Stack align="center" spacing={4}>
              <RingProgress
                size={60}
                roundCaps
                thickness={6}
                sections={[{ value: stat.progress, color: stat.color }]}
                label={
                  <Center>
                    <stat.icon size={16} stroke={1.5} />
                  </Center>
                }
              />

              <Stack align="center" spacing={0}>
                <Text fw={700} size="md">
                  {stat.count}
                </Text>
                <Text
                  c="dimmed"
                  tt="uppercase"
                  fw={600}
                  align="center"
                  style={{ lineHeight: 1.2 }}
                >
                  {stat.label}
                </Text>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    </>
  );
}
